import path from "node:path";
import type { AgentRuntime, ChannelAccountConfig, NoriConfig } from "@mewbleh/nori-core";
import { resolveSecret } from "@mewbleh/nori-core";
import type { NoriPaths, SharedStore } from "@mewbleh/nori-persistence";
import type { ChannelAdapter, ChannelRuntime, InboundMessage, OutboundMessage } from "@mewbleh/nori-plugin-sdk";
import { DiscordChannel } from "./discord.js";
import { TelegramChannel } from "./telegram.js";
import { WhatsAppChannel } from "./whatsapp.js";

type Running = {
  adapter: ChannelAdapter;
  accountId: string;
  config: Record<string, unknown>;
  controller: AbortController;
  handle: { stop(): Promise<void> };
};

function accountsFor(config: NoriConfig, channel: string): Array<{ accountId: string; config: ChannelAccountConfig }> {
  const value = config.channels[channel];
  if (!value) return [];
  const multi = value as { accounts?: Record<string, ChannelAccountConfig> };
  if (multi.accounts) return Object.entries(multi.accounts).map(([accountId, account]) => ({ accountId, config: account }));
  return [{ accountId: "default", config: value }];
}

export class ChannelManager {
  readonly adapterMap = new Map<string, ChannelAdapter>();
  readonly running = new Map<string, Running>();

  constructor(
    readonly config: NoriConfig,
    readonly paths: NoriPaths,
    readonly runtime: AgentRuntime,
    readonly sharedStore: SharedStore,
    adapters: Iterable<ChannelAdapter> = [new TelegramChannel(), new WhatsAppChannel(), new DiscordChannel()],
    readonly env: NodeJS.ProcessEnv = process.env,
  ) {
    for (const adapter of adapters) this.adapterMap.set(adapter.id, adapter);
  }

  adapters(): ChannelAdapter[] {
    return [...this.adapterMap.values()];
  }

  register(adapter: ChannelAdapter): void {
    if (this.adapterMap.has(adapter.id)) throw new Error(`Channel already registered: ${adapter.id}`);
    this.adapterMap.set(adapter.id, adapter);
  }

  status() {
    return [...this.adapterMap.values()].flatMap((adapter) => {
      const accounts = accountsFor(this.config, adapter.id);
      return accounts.length
        ? accounts.map(({ accountId, config }) => ({
            id: adapter.id,
            accountId,
            running: this.running.has(`${adapter.id}:${accountId}`),
            configured: config.enabled !== false,
          }))
        : [{ id: adapter.id, accountId: "default", running: false, configured: false }];
    });
  }

  async #runtimeConfig(channel: string, accountId: string, config: ChannelAccountConfig): Promise<Record<string, unknown>> {
    const token = await resolveSecret(config.token, this.env);
    return {
      ...config,
      ...(token ? { token } : {}),
      accountId,
      ...(channel === "whatsapp" && !config.authDir
        ? { authDir: path.join(this.paths.credentialsDir, "whatsapp", accountId) }
        : {}),
    };
  }

  async start(channel?: string) {
    const adapters = channel ? [this.adapterMap.get(channel)].filter(Boolean) as ChannelAdapter[] : [...this.adapterMap.values()];
    for (const adapter of adapters) {
      for (const { accountId, config } of accountsFor(this.config, adapter.id)) {
        const key = `${adapter.id}:${accountId}`;
        if (config.enabled === false || this.running.has(key)) continue;
        const controller = new AbortController();
        const runtimeConfig = await this.#runtimeConfig(adapter.id, accountId, config);
        const channelRuntime: ChannelRuntime = {
          signal: controller.signal,
          accountId,
          config: runtimeConfig,
          onMessage: async (message) => this.#onMessage(adapter, config, runtimeConfig, message),
          log: (level, message, details) => {
            const output = level === "error" ? process.stderr : process.stdout;
            output.write(`[${adapter.id}:${accountId}] ${level} ${message}${details ? ` ${JSON.stringify(details)}` : ""}\n`);
          },
        };
        const handle = await adapter.start(channelRuntime);
        this.running.set(key, { adapter, accountId, config: runtimeConfig, controller, handle });
      }
    }
    return this.status();
  }

  async probe(channel: string, accountId = "default"): Promise<{ ok: boolean; message?: string }> {
    const adapter = this.adapterMap.get(channel);
    if (!adapter) return { ok: false, message: `Unknown channel: ${channel}` };
    const account = accountsFor(this.config, channel).find((entry) => entry.accountId === accountId);
    if (!account) return { ok: false, message: `Channel account is not configured: ${channel}:${accountId}` };
    return adapter.probe(await this.#runtimeConfig(channel, accountId, account.config));
  }

  async stop(channel?: string) {
    const selected = [...this.running.entries()].filter(([, running]) => !channel || running.adapter.id === channel);
    for (const [key, running] of selected) {
      running.controller.abort();
      await running.handle.stop().catch(() => undefined);
      this.running.delete(key);
    }
    return this.status();
  }

  async logout(channel: string, accountId = "default"): Promise<void> {
    const running = this.running.get(`${channel}:${accountId}`);
    const adapter = this.adapterMap.get(channel);
    if (!adapter?.logout) throw new Error(`Channel does not support logout: ${channel}`);
    await adapter.logout(running?.config ?? { accountId });
  }

  async send(channel: string, accountId: string, message: OutboundMessage): Promise<{ messageId: string }> {
    const running = this.running.get(`${channel}:${accountId}`);
    if (!running) throw new Error(`Channel account is not running: ${channel}:${accountId}`);
    const result = await running.adapter.send(message, {
      signal: running.controller.signal,
      accountId,
      config: running.config,
      log: () => undefined,
    });
    this.sharedStore.completeDelivery(`out:${channel}:${accountId}:${result.messageId}`);
    return result;
  }

  async #onMessage(
    adapter: ChannelAdapter,
    accountConfig: ChannelAccountConfig,
    runtimeConfig: Record<string, unknown>,
    message: InboundMessage,
  ): Promise<void> {
    if (message.sender.isBot) return;
    const deliveryKey = `in:${adapter.id}:${message.conversation.accountId}:${message.id}`;
    if (!this.sharedStore.claimDelivery(deliveryKey, message)) return;
    const dmPolicy = accountConfig.dmPolicy ?? "pairing";
    const allowed = accountConfig.allowFrom?.includes(message.sender.id) || accountConfig.allowFrom?.includes("*");
    if (message.conversation.kind === "dm" && !allowed && !this.sharedStore.isPaired(adapter.id, message.conversation.accountId, message.sender.id)) {
      if (dmPolicy === "disabled" || dmPolicy === "allowlist") return;
      if (dmPolicy === "pairing") {
        const code = this.sharedStore.requestPairing(adapter.id, message.conversation.accountId, message.sender.id);
        await adapter.send(
          { conversation: message.conversation, text: `Nori is locked. Approve this chat with: nori pairing approve ${code}` },
          {
            signal: new AbortController().signal,
            accountId: message.conversation.accountId,
            config: runtimeConfig,
            log: () => undefined,
          },
        );
        return;
      }
    }
    if (message.conversation.kind !== "dm") {
      if ((accountConfig.groupPolicy ?? "allowlist") === "disabled") return;
      const room = accountConfig.groups?.[message.conversation.id] ?? accountConfig.groups?.["*"];
      if (!room && (accountConfig.groupPolicy ?? "allowlist") === "allowlist") return;
      if (room?.requireMention !== false && !message.mentioned) return;
    }
    const agentId = accountConfig.agentId ?? "main";
    const scope = this.config.session.dmScope;
    const peer = message.sender.id;
    const sessionKey =
      scope === "main"
        ? `agent:${agentId}:main`
        : scope === "per-peer"
          ? `agent:${agentId}:peer:${peer}`
          : scope === "per-account-channel-peer"
            ? `agent:${agentId}:${adapter.id}:${message.conversation.accountId}:${peer}`
            : `agent:${agentId}:${adapter.id}:${peer}`;
    let response = "";
    for await (const event of this.runtime.run({
      text: message.text || "[The user sent an attachment without text.]",
      sessionKey,
      agentId,
      channel: adapter.id,
      peerId: peer,
      ...(message.media?.length ? { media: message.media } : {}),
    })) {
      if (event.type === "text-delta") response += event.text;
      if (event.type === "error") response = `I couldn't complete that: ${event.message}`;
    }
    if (response) {
      const sent = await adapter.send(
        { conversation: message.conversation, text: response, replyToId: message.id },
        {
          signal: new AbortController().signal,
          accountId: message.conversation.accountId,
          config: runtimeConfig,
          log: () => undefined,
        },
      );
      this.sharedStore.completeDelivery(`out:${adapter.id}:${message.conversation.accountId}:${sent.messageId}`);
    }
    this.sharedStore.completeDelivery(deliveryKey);
  }
}
